import React from 'react'
import { List, Map, Set } from 'immutable'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import AccommodationCost from './AccommodationCost'
import { withStateHandlers } from 'recompose'
import OfferGroupHeading from './OfferGroupHeading'
import classnames from 'classnames'


/** Renders a single OfferGroup - for an AccommodationRequirement - (potentially within a number of such groups).
 * Independently manages the filters for, and selection within, this offergroup,
 * but with filters showing/hiding controlled externally, in order to show/hide
 * filters across all offer groups with one UI control.
 */
const AccommodationOfferGroup = ({
    offerGroup = Map(),
    accommodationSummaryById = Map(),
    groupNo = 0,
    filterFn,
    passengerCount,
    showHeading = false,

    selectOffer,
    unselectOffer,
    selectedOffers = Set(),

    showUnselected,
    toggleShowUnselected,

}) => {

    const hasSelection = offerGroup.get('offer', List()).some(o =>
        selectedOffers.contains(o.get('uri')))

    // Logic to decide if a given offer is shown or hidden
    // (per-offer flag is set)
    const offers = offerGroup.get('offer', List()).map(o =>
        o.set('match',
            selectedOffers.contains(o.get('uri')) ||
            ((filterFn ? filterFn(o) : true) && (!hasSelection || showUnselected))
        ))

    return <div className="offer-group">
        <OfferGroupHeading showHeading={showHeading} offerGroup={offerGroup} index={groupNo} view="get-a-room">
          {/* If we have a selection, and multiple options to select from */}
          {hasSelection && offers.size > 1 &&
              <span><i className="material-icons"></i>
              <div className="show-hide-controls" style={{float:'right'}}>
                  {!showUnselected &&
                      <button className="btn btn-sm btn-secondary show-unselected" onClick={toggleShowUnselected}>{filterFn
                          ? `Show all (that match filters)`
                          : `Show all ${offers.size}`}
                      </button>
                  }
                  {showUnselected &&
                      <button className="btn btn-sm btn-secondary hide-unselected"
                          onClick={toggleShowUnselected}>Hide unselected</button>
                  }
              </div>
              </span>
          }
        </OfferGroupHeading>
        {offers.map((o = Map()) => {
            const offerSelected = selectedOffers.includes(o.get('uri'))

            // FIXME We will change this to use an ID instead
            const summary = accommodationSummaryById.get(o.getIn(["serviceDetail",0,"uri"]), new Map())
            const ratingClasses = summary.getIn(["rating",0,"rating"],"") === "" ? '' : 'stars-display stars-'+ summary.getIn(["rating",0,"rating"]).replace(" CROWN","")
            return <div  style={{ display: o.get('match') ? 'block' : 'none', cursor: 'pointer'}}
              className={classnames('row','hotel-item','result-group', { 'hoverable' : true, selected : offerSelected })}
              key={o.get('uri')} onClick={() => offerSelected ? unselectOffer(o.get('uri')): selectOffer(o.get('uri'))}>
              <div className="col-lg-4 col-md-5 col-sm-6 thum-col">
                <div className="col-wrapper-va">
                  <img alt={o.getIn(["serviceDetail",0,"serviceProvider","name"])} id={o.getIn(["serviceDetail",0,"uri"])} src={summary.getIn(["principleMedia","url"], "images/imageplaceholder.png")} style={{cursor:'pointer'}}/>
                </div>
              </div>
              <div className="col-lg-16 col-md-14 col-sm-13 details-col">
                <div className="col-wrapper-va">
                  <div className="v-align">
                    <h3>{o.getIn(["serviceDetail",0,"serviceProvider","name"])}</h3>

                    <div className="ratings">
                      {ratingClasses === '' ? <div style={{color:"#ababab",fontSize:"11px"}}><i>rating not available</i></div>
                      : <div className={classnames(ratingClasses)}></div>}
                    </div>
                    {/*<div className="to-details">
                      <span className="hotel-near">Near Two Oceans Aquarium (d)</span>
                    </div>*/}
                    <div className="hotel-address">{summary.getIn(["address","streetAddress"])}</div>
                  </div>
                </div>
              </div>
              <div className="col-sm-5 col-lg-4 price-col">
                  <AccommodationCost
                      cost={o.get('cost')}
                      >
                      <div className="avg-rate">Avg. Nightly Rate {
                        o.get('concrete') ? ``: `from` }</div>
                  </AccommodationCost>
              </div>
              <i className={offerSelected
                  ? 'tl-corner-selected'
                  : 'tl-corner'} style={{left: "5px"}}/>
            </div>
        })
        }
        {(offers.size === 0 || offers.every((o = Map()) => !o.get('match'))) &&
            <div className="no-results-placeholder" key="none">
                No results match your criteria
            </div>
        }
    </div>
}

AccommodationOfferGroup.propTypes = {
    offerGroup: ImmutablePropTypes.map.isRequired,
    // The number of passengers that these offers are for
    // (hack, but this is not in the offerGroups part of the data structure)
    // Instead, probably pass through 'requested' info
    passengerCount: PropTypes.number.isRequired,

    // Map<Accommodation ID, AccommodationSummaryInfo>
    accommodationSummaryById: ImmutablePropTypes.map,

    // Optional filter (predicate function)
    // offerFilter :: Offer -> Bool
    filterFn: PropTypes.func,

    // Add a given offer URI to the user selection
    // selectOffer :: String -> ()
    selectOffer: PropTypes.func,

    // Remove a given offer URI from the user selection
    // unselectOffer :: String -> ()
    unselectOffer: PropTypes.func,

    // Set<OfferURI> - the current user selection
    selectedOffers: ImmutablePropTypes.setOf(PropTypes.string)
}



/* Enhances OfferGroup with a stateful 'showUnselected' property
   and a toggle function, as well as filtering and selecting logic. */
const enhance = withStateHandlers(
    // Initial state
    {
        showUnselected: false
    },
    // Handlers
    {
        toggleShowUnselected: ({ showUnselected }) => () => ({
            showUnselected: !showUnselected
        }),

        // Override behaviour of 'select' and 'unselect' accordingly
        selectOffer: (_, { selectOffer }) => uri => {
            selectOffer(uri)
            return { showUnselected: false }
        },
        unselectOffer: (_, { unselectOffer }) => uri => {
            unselectOffer(uri)
            return { showUnselected: true }
        }
    }
)

export default enhance(AccommodationOfferGroup)



// WEBPACK FOOTER //
// ./src/ndc/offer/AccommodationOfferGroup.js